// Copyright 2021 Greener Power Solutions                 //
// This file is part of the Greener Power Solutions,      //
// and may not be altered, copied, redistributed, or sold //
// without specific permission.                           //
import { useGQLQuery } from '../hooks/useGQLQuery';
import {
  PROJECT_GREENER_UNIT_INSTANCES_KEY,
  READ_PROJECT_GREENER_UNIT_INSTANCES,
} from '../services/gql/projectGreenerUnitInstances';
import { getDateStatus } from '../utils/dates';
import { ProjectContextProps, ProjectInstancesResponse, ProjectProviderProps } from './contexts.types';
import { useGetMicrogrids } from 'features/microgrid/hooks';
import { use404RedirectIf } from 'hooks/use404RedirectIf';
import useAuth from 'hooks/useAuth';
import { useAxiosQuery } from 'hooks/useAxiosQuery';
import { createContext, useMemo } from 'react';
import { PROJECT_CACHE_KEY } from 'services/queryKeys';
import { Project } from 'types';
import { canManageMicrogrids } from 'utils/authorization';

export const ProjectContext = createContext<ProjectContextProps>({
  project: null,
  projectDateStatus: null,
  projectUnitInstances: [],
  projectOrganisations: [],
  projectUnits: [],
  projectMicrogrids: [],
  projectSlug: '',
  isDemo: false,
});

export function ProjectProvider({ projectSlug, children }: ProjectProviderProps) {
  const { user } = useAuth();
  const { data } = useAxiosQuery<Project>(
    PROJECT_CACHE_KEY(projectSlug),
    `/projects/my-projects/?external_identifier__eq=${projectSlug}`
  );
  const projectData = data?.[0];

  use404RedirectIf(!projectData);

  const { projectOrganisation: projectOrganisations, ...project } = projectData || {};

  const { data: projectUnitInstancesData } = useGQLQuery<ProjectInstancesResponse, Error>(
    PROJECT_GREENER_UNIT_INSTANCES_KEY(project?.projectId),
    READ_PROJECT_GREENER_UNIT_INSTANCES,
    { projectId: project?.projectId },
    { enabled: !!projectData }
  );

  const projectUnitInstances = projectUnitInstancesData?.projectGreenerUnitInstances;

  // Get project status
  const projectDateStatus = getDateStatus(project?.tsStart, project?.tsEnd);

  // Extract units from instances
  const projectUnits = useMemo(() => {
    return projectUnitInstances ? projectUnitInstances.map((instance) => instance.greenerUnit) : [];
  }, [projectUnitInstances]);

  const isDemo = import.meta.env.VITE_DEMO_PROJECT_EXTID === project?.externalIdentifier;

  const { microgrids: projectMicrogrids } = useGetMicrogrids({
    projectId: project?.projectId,
    enabled: !!projectData && canManageMicrogrids(user?.role),
  });

  return projectData ? (
    <ProjectContext.Provider
      value={{
        project,
        projectUnitInstances,
        projectOrganisations,
        projectDateStatus,
        projectUnits,
        projectMicrogrids,
        projectSlug,
        isDemo,
      }}
    >
      {children}
    </ProjectContext.Provider>
  ) : null;
}
